/**
 * Open-with-PSD (brief: register Escherllate as a PSD handler so a PSD can be
 * sent/opened straight into it — iOS "Open in…"/share sheet via
 * CFBundleDocumentTypes in Info.plist, Android "Open with…"/share sheet via
 * the VIEW/SEND intent-filters in AndroidManifest.xml).
 *
 * Both platforms deliver an opened/shared file through the SAME channel
 * Capacitor's `App` plugin already exposes for custom-URL-scheme deep links:
 * `App.getLaunchUrl()` reports the URL a cold launch was started with, and the
 * `appUrlOpen` event fires for a warm launch. No extra native code is needed
 * beyond the manifest/plist declarations above.
 *
 * `Filesystem.readFile` accepts an arbitrary file://\/content:// URI directly
 * (no `directory` option) — the documented way to read a file opened/shared
 * into the app this way.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Filesystem } from '@capacitor/filesystem';
import { useUiStore } from '@/stores/ui';

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// Handled-URL dedup, backed by sessionStorage — NOT an in-memory var. Two
// firings must be deduped: (a) getLaunchUrl() and a same-instant appUrlOpen on
// one cold launch; (b) — the important one — a WKWebView RELOAD, which iOS may
// trigger when returning from the share sheet after an Export. A reload re-runs
// initOpenWithPsd() from a fresh JS context (an in-memory guard is wiped) while
// App.getLaunchUrl() still returns the PSD the app was cold-launched with, so
// without a reload-surviving guard the launch PSD silently re-imports and the
// RenderPsdSheet watcher re-renders it. sessionStorage survives a reload within
// one launch but is fresh on a genuine new launch, so opening a PSD still
// imports exactly once per launch.
const HANDLED_KEY = 'esch_handled_psd_urls';

function handledUrls(): string[] {
  try {
    const raw = sessionStorage.getItem(HANDLED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function markHandled(url: string): void {
  try {
    const list = handledUrls();
    if (!list.includes(url)) sessionStorage.setItem(HANDLED_KEY, JSON.stringify([...list, url]));
  } catch {
    // best-effort: if storage is unavailable we degrade to the old
    // in-memory-less behaviour (a reload could re-import), no worse than before.
  }
}

async function handleOpenedUrl(url: string | undefined): Promise<void> {
  if (!url || handledUrls().includes(url)) return;
  markHandled(url);
  const ui = useUiStore();
  try {
    // No `encoding` option ⇒ read as raw binary, returned base64-encoded.
    const { data } = await Filesystem.readFile({ path: url });
    ui.pendingPsdBuffer = base64ToArrayBuffer(data as string);
    ui.showRenderPsd = true;
  } catch (err) {
    // No silent fallback — surface it, even crudely (matches download.ts's
    // native-save failure handling).
    console.error('Could not read the opened PSD:', err);
    alert(`Couldn't open that PSD — ${err instanceof Error ? err.message : String(err)}`);
  }
}

/** Call once at startup (App.vue). No-op on web — file-open association is a
 *  native-platform concept there's no browser equivalent for. */
export function initOpenWithPsd(): void {
  if (!Capacitor.isNativePlatform()) return;
  App.addListener('appUrlOpen', ({ url }) => void handleOpenedUrl(url));
  void App.getLaunchUrl().then((launch) => handleOpenedUrl(launch?.url));
}
